import { useEffect, useState } from "react";
import { X, Clock, PlayCircle, CheckCircle2, AlertCircle, Calendar, ChevronRight, Terminal, Copy, Check, Maximize2, Minimize2 } from "lucide-react";
import baseapi from "../../../services/baseapi"; // Adjust import path
import { showError } from "../../../utils/toast"; // Adjust import path

interface JobDetail {
    id: number;
    created: string;
    updated: string;
    job_id: string;
    process: string;
    reason: string;
    description: string;
    start_time: string;
    end_time: string;
    status: string;
    client_id: number;
}

interface JobDetailsSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    jobId: string | null;
    clientId: number | null;
    jobName: string;
    jobType?: string;
}

export default function JobDetailsSidebar({ isOpen, onClose, jobId, clientId, jobName, jobType }: JobDetailsSidebarProps) {
    const [details, setDetails] = useState<JobDetail[]>([]);
    const [loading, setLoading] = useState(false);
    const [scriptExecData, setScriptExecData] = useState<any>(null);
    const [scriptLoading, setScriptLoading] = useState(false);
    const [copiedStdout, setCopiedStdout] = useState<string | number | null>(null);
    const [copiedStderr, setCopiedStderr] = useState<string | number | null>(null);
    const [isExpanded, setIsExpanded] = useState(false);

    const isExecuteScript = ['execute script', 'install application'].includes(jobType?.toLowerCase().replace(/_/g, ' ') || '');

    const handleCopy = (text: string, type: 'stdout' | 'stderr', id: string | number) => {
        navigator.clipboard.writeText(text);
        if (type === 'stdout') {
            setCopiedStdout(id);
            setTimeout(() => setCopiedStdout(null), 2000);
        } else {
            setCopiedStderr(id);
            setTimeout(() => setCopiedStderr(null), 2000);
        }
    };

    useEffect(() => {
        if (isOpen && jobId && clientId) {
            fetchJobDetails();
            if (isExecuteScript) {
                fetchScriptExecution();
            }
        } else if (!isOpen) {
            // Clear details when closed for a clean slide-out
            setTimeout(() => {
                setDetails([]);
                setScriptExecData(null);
            }, 300);
        }
    }, [isOpen, jobId, clientId, jobType]);

    const fetchJobDetails = async () => {
        setLoading(true);
        try {
            const response = await baseapi.get("/job_details", {
                params: { query: JSON.stringify({ job_id: jobId, client_id: clientId }) }
            });

            if (response.data.status) {
                setDetails(response.data.data);
            } else {
                showError(response.data.message || "Failed to load job details");
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchScriptExecution = async () => {
        if (!jobId) return;
        setScriptLoading(true);
        try {
            const response = await baseapi.get("/script_executions/", {
                params: { query: JSON.stringify({ job_id: jobId }) }
            });
            if (response.data.status) {
                setScriptExecData(response.data.data);
            } else {
                setScriptExecData(response.data);
            }
        } catch (error: any) {
            console.error("Error fetching script execution:", error);
            setScriptExecData({ error: error.message || "Failed to fetch script execution details" });
        } finally {
            setScriptLoading(false);
        }
    };

    const getStatusConfig = (status: string) => {
        const normalized = status.toLowerCase();
        if (normalized === "completed" || normalized === "success") {
            return { icon: CheckCircle2, color: "text-emerald-400", bg: "bg-emerald-500/20", border: "border-emerald-500/30" };
        } else if (normalized === "failed" || normalized === "error") {
            return { icon: AlertCircle, color: "text-red-400", bg: "bg-red-500/20", border: "border-red-500/30" };
        } else if (normalized === "running" || normalized === "in_progress") {
            return { icon: PlayCircle, color: "text-blue-400", bg: "bg-blue-500/20", border: "border-blue-500/30", pulse: true };
        }
        return { icon: Clock, color: "text-gray-400", bg: "bg-gray-700/50", border: "border-gray-600" };
    };

    const formatTime = (timeStr: string) => {
        if (!timeStr) return "-";
        const date = new Date(timeStr);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) + ' ' + date.toLocaleDateString();
    };

    return (
        <>
            {/* Backdrop */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity"
                    onClick={onClose}
                />
            )}

            {/* Sliding Sidebar */}
            <div
                className={`fixed top-0 right-0 h-full ${isExpanded ? "w-[950px]" : "w-[600px]"} bg-gray-900 border-l border-gray-700 shadow-2xl z-50 transform transition-all duration-300 ease-in-out flex flex-col ${isOpen ? "translate-x-0" : "translate-x-full"
                    }`}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-800 bg-gray-900/95 sticky top-0 z-10">
                    <div>
                        <h2 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
                            Job Details
                        </h2>
                        <p className="text-[11px] text-gray-400 mt-1 truncate max-w-[300px]">
                            {jobName ? jobName.replace(/_/g, ' ') : jobId}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
                            title={isExpanded ? "Collapse" : "Expand"}
                        >
                            {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Timeline Content */}
                <div className="flex-1 overflow-y-auto custom-scroll p-6 bg-gradient-to-b from-gray-900 to-gray-950">
                    {isExecuteScript && (
                        <div className="space-y-4 mb-6">
                            {scriptLoading ? (
                                <div className="flex flex-col items-center justify-center py-6 gap-2">
                                    <div className="w-5 h-5 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                                    <span className="text-[9px] text-gray-500 font-bold uppercase tracking-wider">Fetching script output...</span>
                                </div>
                            ) : scriptExecData ? (
                                (() => {
                                    const executions = Array.isArray(scriptExecData)
                                        ? scriptExecData
                                        : [scriptExecData];

                                    if (executions.length === 0) {
                                        return (
                                            <div className="bg-gray-800/20 border border-gray-700/30 rounded-xl p-4 text-center">
                                                <p className="text-[10px] text-gray-500 italic">No script execution details found.</p>
                                            </div>
                                        );
                                    }

                                    return executions.map((exec: any, index: number) => {
                                        const execId = exec.id || exec.job_id || index.toString();
                                        const isExitSuccess = exec.exit_code === 0 || exec.exit_code === "0";

                                        const isSuccess = isExitSuccess || exec.status?.toLowerCase() === 'success';
                                        const statusBg = isSuccess
                                            ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                                            : 'bg-red-500/10 border-red-500/20 text-red-400';

                                        return (
                                            <div key={execId} className="bg-gray-800/40 border border-gray-700/50 hover:border-gray-600 rounded-xl p-4 transition-all duration-200 shadow-sm space-y-3">
                                                {/* Header info */}
                                                <div className="flex items-start justify-between gap-3">
                                                    <div>
                                                        <h3 className="text-xs font-bold text-gray-200 flex items-center gap-1.5">
                                                            <Terminal size={12} className="text-orange-400 shrink-0" />
                                                            {exec.script_name || 'Script Execution'}
                                                        </h3>
                                                        <p className="text-[9px] text-gray-500 mt-0.5">
                                                            Duration: <span className="font-semibold text-gray-300">{exec.duration_ms} ms</span>
                                                        </p>
                                                    </div>
                                                    <div className="flex flex-col items-end gap-1">
                                                        <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${statusBg}`}>
                                                            {exec.status || (isExitSuccess ? 'success' : 'failed')}
                                                        </span>
                                                        <span className="text-[8px] font-mono text-gray-500">
                                                            Exit Code: <span className={isExitSuccess ? 'text-emerald-500 font-bold' : 'text-red-500 font-bold'}>{exec.exit_code}</span>
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Stdout Output */}
                                                {exec.stdout && (
                                                    <div className="space-y-1 relative group/stdout">
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-[9px] font-bold text-gray-400 block uppercase tracking-wider">Output (stdout)</span>
                                                            <button
                                                                onClick={() => handleCopy(exec.stdout, 'stdout', execId)}
                                                                className="p-1 text-gray-400 hover:text-white bg-gray-800 rounded opacity-0 group-hover/stdout:opacity-100 transition-opacity"
                                                                title="Copy output"
                                                            >
                                                                {copiedStdout === execId ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                                                            </button>
                                                        </div>
                                                        <pre className="text-[10px] text-gray-300 bg-gray-950/95 p-3 rounded-lg overflow-x-auto border border-gray-800/80 font-mono max-h-60 custom-scroll select-text leading-relaxed whitespace-pre-wrap">
                                                            {exec.stdout}
                                                        </pre>
                                                    </div>
                                                )}

                                                {/* Stderr Output */}
                                                {exec.stderr && (
                                                    <div className="space-y-1 relative group/stderr">
                                                        <div className="flex items-center justify-between">
                                                            <span className={`text-[9px] font-bold block uppercase tracking-wider ${isExitSuccess ? 'text-amber-400' : 'text-red-400'}`}>
                                                                {isExitSuccess ? 'Info (stderr)' : 'Error (stderr)'}
                                                            </span>
                                                            <button
                                                                onClick={() => handleCopy(exec.stderr, 'stderr', execId)}
                                                                className="p-1 text-gray-400 hover:text-white bg-gray-800 rounded opacity-0 group-hover/stderr:opacity-100 transition-opacity"
                                                                title={isExitSuccess ? 'Copy info' : 'Copy error'}
                                                            >
                                                                {copiedStderr === execId ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                                                            </button>
                                                        </div>
                                                        <pre className={`text-[10px] p-3 rounded-lg overflow-x-auto font-mono max-h-60 custom-scroll select-text leading-relaxed whitespace-pre-wrap ${
                                                            isExitSuccess
                                                                ? 'text-amber-300 bg-amber-950/20 border border-amber-900/30'
                                                                : 'text-red-400 bg-red-950/20 border border-red-900/20'
                                                        }`}>
                                                            {exec.stderr}
                                                        </pre>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    });
                                })()
                            ) : (
                                <div className="bg-gray-800/20 border border-gray-700/30 rounded-xl p-4 text-center">
                                    <p className="text-[10px] text-gray-500 italic">No script execution data returned.</p>
                                </div>
                            )}
                        </div>
                    )}
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-full space-y-3">
                            <div className="w-8 h-8 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Loading Timeline...</p>
                        </div>
                    ) : details.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-gray-500 opacity-60">
                            <Calendar size={32} className="mb-2" />
                            <p className="text-[10px] font-bold uppercase tracking-widest">No timeline data available</p>
                        </div>
                    ) : (
                        <div className="relative border-l-2 border-gray-700/50 ml-3 pb-4">
                            {details.map((detail, _index) => {
                                const { icon: Icon, color, bg, border, pulse } = getStatusConfig(detail.status);

                                return (
                                    <div key={detail.id} className="mb-8 relative pl-6 last:mb-0 group">
                                        {/* Timeline Dot/Icon */}
                                        <div className={`absolute -left-[17px] top-1 h-8 w-8 rounded-full border-4 border-gray-900 flex items-center justify-center ${bg} ${color} ${pulse ? 'animate-pulse' : ''}`}>
                                            <Icon size={14} />
                                        </div>

                                        {/* Content Card */}
                                        <div className="bg-gray-800/40 hover:bg-gray-800/80 border border-gray-700/50 hover:border-gray-600 rounded-xl p-4 transition-all duration-200 shadow-sm">
                                            <div className="flex items-start justify-between gap-3 mb-2">
                                                <h3 className="text-xs font-bold text-gray-200">
                                                    {detail.process.replace(/_/g, ' ')}
                                                </h3>
                                                <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${bg} ${border} border ${color}`}>
                                                    {detail.status.replace(/_/g, ' ')}
                                                </span>
                                            </div>

                                            <p className="text-[11px] text-gray-400 leading-relaxed mb-3">
                                                {detail.description}
                                            </p>

                                            <div className="flex items-center gap-4 text-[9px] font-medium text-gray-500 bg-gray-900/50 p-2 rounded-lg">
                                                <div className="flex items-center gap-1.5">
                                                    <Clock size={12} className="text-gray-400" />
                                                    <span>{formatTime(detail.start_time)}</span>
                                                </div>
                                                {detail.end_time && (
                                                    <>
                                                        <ChevronRight size={10} className="text-gray-600" />
                                                        <div className="flex items-center gap-1.5">
                                                            <CheckCircle2 size={12} className="text-emerald-500/70" />
                                                            <span>{formatTime(detail.end_time)}</span>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}